import React, { useEffect, useState } from 'react';
import {
  DataGridPro,
  GridColDef,
  GridOverlay,
  GridFilterModel,
  GridSortModel,
  getGridDateOperators,
} from '@mui/x-data-grid-pro';
import { Card, Chip } from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { green, orange } from '@mui/material/colors';

import BooleanFilterComponent from './BooleanFilter';
import {
  AdminResponseFilter,
  CertificationFilter,
  CheckOutcomeFilter,
  CheckTypeFilter,
  StatusFilter,
} from './ScreeningFilters';
import ScreeningRunDetailDialog from './Modals/ScreeningRunDetailDialog';
import { ScreeningRun } from '../../interfaces/screening.interface';
import { getScreeningRunsForDataGrid } from '../../services/screening';
import { CHECK_TYPE_LABELS, STATUS_LABELS } from './screeningLabels';
import { formatDate, normalizeFilterModel, stringContainsOnly } from '../../utils/adminDashboardUtils';

function hasFlaggedCheck(run: ScreeningRun): boolean {
  return run.checks?.some((check) => check.outcome === 'flag') ?? false;
}

function flaggedChecks(run: ScreeningRun) {
  return run.checks?.filter((check) => check.outcome === 'flag') ?? [];
}

const columns: GridColDef[] = [
  { field: 'id', headerName: 'ID', width: 70, filterOperators: stringContainsOnly },
  {
    field: 'company',
    headerName: 'Company',
    width: 200,
    filterOperators: stringContainsOnly,
    valueGetter: (params) => params.row.companyName,
  },
  {
    field: 'certification',
    headerName: 'Certification',
    width: 180,
    valueGetter: (params) => params.row.certificationTitle,
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: CertificationFilter,
    }],
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 130,
    renderCell: (params) => STATUS_LABELS[params.row.status] ?? params.row.status,
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: StatusFilter,
    }],
  },
  {
    field: 'has_flag',
    headerName: 'Flag',
    width: 90,
    sortable: false,
    valueGetter: (params) => hasFlaggedCheck(params.row as ScreeningRun),
    renderCell: (params) => (params.value
      ? <Chip size="small" label="Flag" sx={{ backgroundColor: orange[700], color: '#fff' }} />
      : <Chip size="small" label="Clean" sx={{ backgroundColor: green[600], color: '#fff' }} />),
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: (filterItem) => ({ value }) => value === filterItem.value,
      InputComponent: BooleanFilterComponent,
    }],
  },
  {
    field: 'is_current',
    headerName: 'Current',
    width: 100,
    valueGetter: (params) => params.row.isCurrent,
    renderCell: (params) => (params.value ? 'Yes' : 'No'),
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: (filterItem) => ({ value }) => value === filterItem.value,
      InputComponent: BooleanFilterComponent,
    }],
  },
  {
    field: 'admin_reviewed',
    headerName: 'Admin Reviewed',
    width: 140,
    valueGetter: (params) => params.row.adminReviewed,
    renderCell: (params) => (params.value ? 'Yes' : 'No'),
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: (filterItem) => ({ value }) => value === filterItem.value,
      InputComponent: BooleanFilterComponent,
    }],
  },
  {
    field: 'admin_approved',
    headerName: 'Admin Approved',
    width: 140,
    valueGetter: (params) => params.row.adminApproved,
    renderCell: (params) => (params.value ? 'Yes' : 'No'),
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: (filterItem) => ({ value }) => value === filterItem.value,
      InputComponent: BooleanFilterComponent,
    }],
  },
  {
    field: 'check_type',
    headerName: 'Flagged Checks',
    width: 210,
    sortable: false,
    // Names of the checks that flagged, or an em dash for a clean run.
    valueGetter: (params) => flaggedChecks(params.row as ScreeningRun)
      .map((check) => CHECK_TYPE_LABELS[check.checkType] ?? check.checkType)
      .join(', ') || '—',
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: CheckTypeFilter,
    }],
  },
  {
    field: 'check_outcome',
    headerName: 'Check Outcome',
    width: 160,
    sortable: false,
    renderCell: (params) => {
      const run = params.row as ScreeningRun;
      const flagCount = flaggedChecks(run).length;
      if (flagCount > 0) {
        return (
          <Chip
            size="small"
            label={flagCount === 1 ? '1 Flag' : `${flagCount} Flags`}
            sx={{ backgroundColor: orange[700], color: '#fff' }}
          />
        );
      }
      if (run.checks?.some((check) => check.outcome === 'not_evaluated')) {
        return <Chip size="small" label="Not Evaluated" />;
      }
      return <Chip size="small" label="All Pass" sx={{ backgroundColor: green[600], color: '#fff' }} />;
    },
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: CheckOutcomeFilter,
    }],
  },
  {
    field: 'admin_response',
    headerName: 'Admin Response',
    width: 160,
    sortable: false,
    // Progress over the flagged checks: "1 / 2" = one accept/dismiss recorded of two flags.
    valueGetter: (params) => {
      const flagged = flaggedChecks(params.row as ScreeningRun);
      if (!flagged.length) return '—';
      const responded = flagged.filter((check) => check.adminResponse).length;
      return `${responded} / ${flagged.length}`;
    },
    filterOperators: [{
      label: 'Is',
      value: 'is',
      getApplyFilterFn: () => null,
      InputComponent: AdminResponseFilter,
    }],
  },
  {
    field: 'created_at',
    headerName: 'Created',
    type: 'dateTime',
    filterOperators: getGridDateOperators().filter((op) => op.value === 'is'),
    width: 130,
    valueGetter: (params) => formatDate(params.row.createdAt),
    renderCell: (params) => formatDate(params.row.createdAt),
  },
];

export default function ScreeningAdminPanel() {
  const [rows, setRows] = useState<ScreeningRun[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [selectedRun, setSelectedRun] = useState<ScreeningRun | null>(null);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);

  const [sortModel, setSortModel] = useState<GridSortModel>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });

  const fetchAndSetRows = async () => {
    try {
      const data = await getScreeningRunsForDataGrid({ page, pageSize, sortModel, filterModel });
      setRows(data.results);
      setRowCount(data.count);
    } catch (err) {
      console.error('Error fetching screening runs', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchAndSetRows();
  }, [page, pageSize, sortModel, filterModel]);

  const handleRunUpdated = (updatedRun: ScreeningRun) => {
    setSelectedRun(updatedRun);
    setRows((prevRows) => prevRows.map((row) => (row.id === updatedRun.id ? updatedRun : row)));
  };

  const actionsColumn: GridColDef = {
    field: 'actions',
    headerName: 'Actions',
    width: 100,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <Button size="small" startIcon={<VisibilityIcon />} onClick={() => setSelectedRun(params.row as ScreeningRun)}>
        View
      </Button>
    ),
  };

  return (
    <Card sx={{ padding: '2em' }}>
      <DataGridPro
        style={{ height: 500, width: '100%' }}
        rows={rows}
        columns={[...columns, actionsColumn]}
        pagination
        paginationMode="server"
        page={page}
        pageSize={pageSize}
        rowsPerPageOptions={[25, 50, 100, 200]}
        rowCount={rowCount}
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setPage(0);
          setSortModel(model);
        }}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={(model) => {
          const normalized = normalizeFilterModel(model);
          const prev = JSON.stringify(filterModel.items ?? []);
          const next = JSON.stringify(normalized.items ?? []);

          if (prev === next) return;

          setPage(0);
          setFilterModel(normalized);
        }}
        loading={isLoading}
        components={{
          LoadingOverlay: () => (
            <GridOverlay>
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
                <CircularProgress />
              </div>
            </GridOverlay>
          ),
        }}
      />
      <ScreeningRunDetailDialog
        isOpen={!!selectedRun}
        run={selectedRun}
        handleClose={() => setSelectedRun(null)}
        onRunUpdated={handleRunUpdated}
      />
    </Card>
  );
}
